/**
 * The records of `backgrounds` (ADR-136's amendment of 2026-10-28, decision 6): each background of Bible 4.2 names at
 * most one attribute whose dialogue checks it adds its bonus to, and the bonus it adds.
 */
import { DIALOGUE_CHECK_ATTRIBUTES, type DialogueCheckAttribute } from "../npcs/dialogue-marks.ts";
import { ATTRIBUTES, isAttribute, type Attribute } from "./attributes.ts";
import { fieldsOf, isPlainRecord, oneOf, report, whole, type Place } from "./checks.ts";
import { BACKGROUND_IDS, BACKGROUND_STARTING_POINTS, MAX_BACKGROUND_CHECK_BONUS, type Background, type BackgroundId } from "./types.ts";

/**
 * Checks a background's starting attributes (ADR-138 decision 4): one or two of the seven attributes, each with a whole
 * number of points from 1 to `BACKGROUND_STARTING_POINTS`, the points summing to `BACKGROUND_STARTING_POINTS`.
 *
 * @param place Where to report.
 * @param value The value.
 * @returns The map, or null.
 */
function startingAttributesOf(place: Place, value: unknown): Readonly<Partial<Record<Attribute, number>>> | null {
  if (!isPlainRecord(value)) {
    report(place, "startingAttributes must be an object of attribute to points");
    return null;
  }
  const names = Object.keys(value);
  const read: Partial<Record<Attribute, number>> = {};
  let ok = names.length >= 1 && names.length <= 2;
  let total = 0;
  for (const attribute of names) {
    if (!isAttribute(attribute)) {
      report(place, `startingAttributes.${attribute} is not an attribute (${ATTRIBUTES.join(", ")})`);
      ok = false;
      continue;
    }
    const points = whole(place, value[attribute], `startingAttributes.${attribute}`, 1, BACKGROUND_STARTING_POINTS);
    if (points === null) {
      ok = false;
    } else {
      read[attribute] = points;
      total += points;
    }
  }
  if (ok && total !== BACKGROUND_STARTING_POINTS) {
    report(place, `startingAttributes adds up to ${total} points, and a background gives ${BACKGROUND_STARTING_POINTS}`);
    ok = false;
  }
  if (names.length < 1 || names.length > 2) {
    report(place, `startingAttributes holds ${names.length} attributes, and a background gives points to one or two`);
  }
  return ok ? read : null;
}

/**
 * Checks a background: its id, one of Bible 4.2's six; the attribute it helps, one of the seven or null; its bonus, a
 * whole number from 0 to `MAX_BACKGROUND_CHECK_BONUS`; and the attributes it starts a character with.
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The background, or null.
 */
export function backgroundOf(place: Place, record: Readonly<Record<string, unknown>>): Background | null {
  const fields = fieldsOf(place, record, "", ["id", "checkAttribute", "checkBonus", "startingAttributes"], "a background") as Readonly<Record<string, unknown>>;
  const id = oneOf(place, fields["id"], "id", BACKGROUND_IDS, "a background");
  const attribute = fields["checkAttribute"] === null ? null : oneOf(place, fields["checkAttribute"], "checkAttribute", DIALOGUE_CHECK_ATTRIBUTES, "an attribute");
  const bonus = whole(place, fields["checkBonus"], "checkBonus", 0, MAX_BACKGROUND_CHECK_BONUS);
  const starting = startingAttributesOf(place, fields["startingAttributes"]);
  const attributeRead = fields["checkAttribute"] === null || attribute !== null;
  return id === null || !attributeRead || bonus === null || starting === null || Object.keys(fields).length !== 4 ? null : { id, checkAttribute: attribute, checkBonus: bonus, startingAttributes: starting };
}

/**
 * What a character's background adds to the attribute a dialogue check reads, in attribute points (the author's answer
 * of 2026-10-28: +1 to the attribute, so +5 percent through the formula): the background's `checkBonus` when the check
 * reads its attribute, else nothing.
 *
 * @param backgrounds The `backgrounds` table.
 * @param background The character's background id, or null for a character with none.
 * @param attribute The attribute the check reads.
 * @returns The bonus in attribute points, 0 when the background helps another attribute or none.
 * @example
 * backgroundCheckBonus(rules.tables.backgrounds, "wanderer", "vitality");
 * // 1
 */
export function backgroundCheckBonus(backgrounds: readonly Background[], background: BackgroundId | null, attribute: DialogueCheckAttribute): number {
  const row = background === null ? undefined : backgrounds.find((entry) => entry.id === background);
  return row !== undefined && row.checkAttribute === attribute ? row.checkBonus : 0;
}
